/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Dispatch, SetStateAction, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { DocumentWithTag } from 'blb-ui-types/BridgeTypes';
import { buttonReset } from 'blb-styles/mixins';
import { compact, constant, map, sortBy, times, uniq, zipObject } from 'lodash';
import { BrandColors, Checkbox, MaterialIcon, LenderTable, Text, Tag, Header } from '@blend/enterprise-components';
import { getFormattedDisplay } from 'blb-utils/date';
import { removeExtensionFromString } from 'blb-utils/documents';

import { isDocDownloadable } from '../../../docUtils';

const styles = StyleSheet.create({
  buttonWrapper: {
    ...buttonReset(),

    backgroundColor: BrandColors.CLOUD_30,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
    textAlign: 'left',
    width: '100%',
  },
  cluster: {
    marginBottom: 24,
  },
  tagWrapper: {
    marginRight: 16,
  },
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
});

type TwoColumn<T> = [T, T];

const columnWidths: TwoColumn<number> = [0.2, 2];

export namespace DocRowCluster {
  export interface Props {
    disabled: boolean;
    documentCluster: DocumentWithTag[];
    isFirst: boolean;
    isUploaded: boolean;
    selectedDocs: Record<string, boolean>;
    sentOn: string;
    setSelectedDocs: Dispatch<SetStateAction<Record<string, boolean>>>;
  }
}

export const DocRowCluster: React.FC<DocRowCluster.Props> = ({
  disabled,
  isFirst,
  documentCluster,
  selectedDocs,
  isUploaded,
  sentOn,
  setSelectedDocs,
}) => {
  const [isOpen, setIsOpen] = useState(isFirst);
  const sentDate = Number(sentOn);

  const sortedDocumentCluster = sortBy(documentCluster, doc => (isDocDownloadable(doc) ? -1 : 1));
  const clusterDocIds = compact(map(sortedDocumentCluster, doc => isDocDownloadable(doc) && doc._id));
  const areSomeClusterDocsSelected = compact(clusterDocIds.map((id: string) => selectedDocs[id])).length;

  /**
   * Create a map where each documentId is mapped to the same boolean value
   * @param flag - boolean
   */
  const updateAllClusterDocs = (flag: boolean) => zipObject(clusterDocIds, times(clusterDocIds.length, constant(flag)));

  const markAllClusterDocs = (flag: boolean) => setSelectedDocs({ ...selectedDocs, ...updateAllClusterDocs(flag) });

  const { checked, icon, onHeaderClick } = areSomeClusterDocsSelected
    ? {
        checked: true,
        icon: 'dash' as const,
        onHeaderClick: () => markAllClusterDocs(false),
      }
    : {
        checked: false,
        icon: 'check' as const,
        onHeaderClick: () => markAllClusterDocs(true),
      };

  const headerComponents: TwoColumn<React.ReactNode> = [
    <Checkbox
      checked={checked}
      disabled={disabled}
      icon={icon}
      id={`it-BulkSelector-${sentOn}`}
      key="headerSelector"
      onChange={onHeaderClick}
    />,
    <Header ellipsis key="docNameHeader" size={6}>
      Document Name
    </Header>,
  ];

  const getDocumentTag = (doc: DocumentWithTag) => {
    return isDocDownloadable(doc) ? (
      doc.sentInMostRecentBatch && (
        <div className={css(styles.tagWrapper)}>
          <Tag text="ADDED" />
        </div>
      )
    ) : (
      <div className={css(styles.tagWrapper)}>
        <Tag text="REMOVED" />
      </div>
    );
  };

  const getSelectDocForDownloadCheckBox = (doc: DocumentWithTag) => {
    return isDocDownloadable(doc) ? (
      <Checkbox
        checked={!!selectedDocs[doc._id]}
        disabled={disabled}
        id={`it-SelectDoc-${doc._id}`}
        onChange={() => setSelectedDocs(prevState => ({ ...prevState, [doc._id]: !prevState[doc._id] }))}
      />
    ) : (
      <Checkbox checked={false} disabled={true} id={`it-SelectDoc-${doc._id}`} />
    );
  };

  const sections = map(sortedDocumentCluster, doc => {
    const title = uniq([doc.displayFileName, !isUploaded && removeExtensionFromString(doc.fileName)])
      .filter(Boolean)
      .join(' - ');
    const docTitle = (
      <div className={css(styles.titleWrapper)} id={`it-doc-${doc._id}`}>
        {getDocumentTag(doc)}
        <Text weight="semibold">{title}</Text>
      </div>
    );

    return [getSelectDocForDownloadCheckBox(doc), docTitle];
  });

  return (
    <div className={css(styles.cluster)}>
      <button
        className={css(styles.buttonWrapper)}
        id={`it-DocRowCluster-toggle-${sentOn}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Text weight="medium">
          {sentDate === Number.POSITIVE_INFINITY
            ? 'Not sent yet'
            : `${isUploaded ? 'Uploaded' : 'Sent'} on ${getFormattedDisplay(sentDate, 'MM/dd/yyyy h:mma')}`}
        </Text>
        <MaterialIcon color={BrandColors.NIGHT_90} size={24} type={isOpen ? 'expand_less' : 'expand_more'} />
      </button>
      {isOpen && (
        <LenderTable
          columnWidths={columnWidths}
          headerComponents={headerComponents}
          rightAlignLastCol={false}
          sections={sections}
        />
      )}
    </div>
  );
};
